import { App } from "vue";
import { io, Socket } from "socket.io-client";
import JwtService from "@/services/JwtService";

/**
 * @description service to handle socket connections
 */
class SocketService {
    /**
     * @description property to share vue instance
     */
    public static vueInstance: App<Element>;

    /**
     * @description Socket.io client instance
     */
    private static socket: Socket;

    /**
     * @description initialize socket connection
     */
    public static init(app: App<Element>): void {
        SocketService.vueInstance = app;
        
        const baseUrl = 'https://137.255.12.34:3003';
        SocketService.socket = io(baseUrl, {
            transports: ['websocket'],
            auth: {
                token: JwtService.getToken(),
            },
            autoConnect: true,
        });

        SocketService.vueInstance.config.globalProperties.$socket = SocketService.socket;

        // Event listeners
        SocketService.socket.on('connect', () => {
            console.log('Socket connected:', SocketService.socket.id);
        });

        SocketService.socket.on('disconnect', () => {
            console.log('Socket disconnected');
        });

        SocketService.socket.on('connect_error', (error: any) => {
            console.error('Socket connection error:', error);
        });

        SocketService.socket.on('error', (error: any) => {
            console.error('Socket error:', error);
        });
    }

    /**
     * @description send a message to a specific channel
     * @param channel: string
     * @param data: any
     */
    public static emit(channel: string, data: any): void {
        if (SocketService.socket && SocketService.socket.connected) {
            SocketService.socket.emit(channel, data);
        } else {
            console.error('Socket is not connected');
        }
    }

    /**
     * @description listen for messages from a specific channel
     * @param channel: string
     * @param callback: function
     */
    public static on(channel: string, callback: (data: any) => void): void {
        if (SocketService.socket) {
            SocketService.socket.on(channel, callback);
        }
    }

    /**
     * @description remove listener for a specific channel
     * @param channel: string
     * @param callback?: function
     */
    public static off(channel: string, callback?: (data: any) => void): void {
        if (SocketService.socket) {
            if (callback) {
                SocketService.socket.off(channel, callback);
            } else {
                SocketService.socket.off(channel);
            }
        }
    }

    /**
     * @description join a room
     * @param room: string
     */
    public static joinRoom(room: string): void {
        if (SocketService.socket && SocketService.socket.connected) {
            SocketService.socket.emit('join', room);
        }
    }

    /**
     * @description leave a room
     * @param room: string
     */
    public static leaveRoom(room: string): void {
        if (SocketService.socket && SocketService.socket.connected) {
            SocketService.socket.emit('leave', room);
        }
    }

    /**
     * @description disconnect the socket connection
     */
    public static disconnect(): void {
        if (SocketService.socket && SocketService.socket.connected) {
            SocketService.socket.disconnect();
        }
    }

    /**
     * @description reconnect the socket connection
     */
    public static reconnect(): void {
        if (SocketService.socket && !SocketService.socket.connected) {
            SocketService.socket.connect();
        }
    }

    /**
     * @description check if socket is connected
     */
    public static isConnected(): boolean {
        return SocketService.socket && SocketService.socket.connected;
    }
}

export default SocketService;
